/**
 * Shared "Paid from / Paid to" form for pass-through payments (BE-3 work order items, BE-3d direct invoice fees).
 *
 * Sources: the TC's bank, cash and e-wallet accounts, its credit cards, and the customer's saved cards. The form value
 * `from` is `ACCOUNT:<id>`, `TCCARD:<id>` or `CUSTCARD:<id>` (an empty id when the customer has no saved card).
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert, TextField, MenuItem, ListSubheader } from '@mui/material';
import ApiService from '../../services/api.service';
import internalServiceService from '../../services/internal-service.service';
import { creditCardService } from '../../services/accounting/creditCard.service';
import { customerCardService } from '../../services/accounting/customerCard.service';
import { serviceProviderService } from '../../services/accounting/serviceProvider.service';
import { unwrapList } from '../../utils/apiConvert';
import { cardLabel } from '../../pages/accounting/CreditCards';

export const AUTH_TYPES = { SIGNED_SERVICE_ORDER: 'Signed service order', CUSTOMER_APPROVAL: 'Customer approval (written)', VERBAL: 'Verbal — finance reviews it' };
export const EVIDENCE = {
  ACCEPTED: { tone: 'success', label: 'Evidence accepted' },
  PENDING_REVIEW: { tone: 'warning', label: 'Waiting for review' },
  REJECTED: { tone: 'error', label: 'Evidence rejected' },
};
export const PAYABLE_TYPES = ['OPERATIONS', 'CASH', 'E_WALLET'];
export const today = () => new Date().toISOString().slice(0, 10);
const pick = (o, camel, snake) => (o?.[camel] !== undefined ? o[camel] : o?.[snake]);

export const accountTypeOf = (a) => pick(a, 'accountType', 'account_type');
export const accountTitle = (a) => [pick(a, 'bankName', 'bank_name'), pick(a, 'accountName', 'account_name')].filter(Boolean).join(' - ');
export const emptyPayForm = () => ({ from: '', paidTo: '', paidToName: '', receiptRef: '', paidAt: today(), reason: '' });

// The "Paid from" to pre-select: the service's own account when still usable, else the default account, else nothing.
export const defaultPaidFrom = (accounts, serviceAccountId) => {
  const usable = accounts.filter((a) => a.isActive !== false && PAYABLE_TYPES.includes(accountTypeOf(a)));
  const own = serviceAccountId && usable.find((a) => a.id === serviceAccountId);
  const chosen = own || usable.find((a) => pick(a, 'isDefault', 'is_default'));
  return chosen ? `ACCOUNT:${chosen.id}` : '';
};

// The TC's own money that left: these need an outcome when the line or document goes away.
export const needsOutcome = (p) => p.status === 'RECORDED' && p.source !== 'CUSTOMER_CARD' && p.source !== 'PARTNER';

export const usePaymentLookups = (open, orgId) => {
  const [accounts, setAccounts] = useState([]);
  const [tcCards, setTcCards] = useState([]);
  const [customerCards, setCustomerCards] = useState([]);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    if (!open) return;
    ApiService.getTcBankAccounts().then(unwrapList).then((r) => setAccounts(r.items)).catch(() => {});
    creditCardService.getCards().then(setTcCards).catch(() => {});
    serviceProviderService.list().then(setProviders).catch(() => {});
    if (orgId) customerCardService.getForOrganization(orgId).then(setCustomerCards).catch(() => setCustomerCards([]));
    else setCustomerCards([]);
  }, [open, orgId]);

  return { accounts, tcCards, customerCards, providers };
};

// The paid-from account set on a catalog service; null when unset or the id is not a catalog service (e.g. a Thaklis one).
export const useServiceAccountId = (open, serviceId) => {
  const [accountId, setAccountId] = useState(null);

  useEffect(() => {
    setAccountId(null);
    if (!open || !serviceId) return undefined;
    let active = true;
    internalServiceService.getById(serviceId)
      .then((s) => { if (active) setAccountId(s?.bank_account_id ?? null); })
      .catch(() => {});
    return () => { active = false; };
  }, [open, serviceId]);

  return accountId;
};

// Request body for a record / replace: source and payee from the form; the caller adds the component.
export const buildPaymentBody = (form, { accounts }) => {
  const [kind, id] = form.from.split(':');
  const body = { receiptRef: form.receiptRef.trim(), paidAt: form.paidAt };
  if (kind === 'ACCOUNT') {
    const t = accountTypeOf(accounts.find((a) => a.id === id));
    Object.assign(body, { source: t === 'CASH' ? 'CASH' : t === 'E_WALLET' ? 'E_WALLET' : 'TC_BANK', sourceAccountId: id });
  } else if (kind === 'TCCARD') Object.assign(body, { source: 'TC_CREDIT_CARD', sourceCardId: id });
  else Object.assign(body, { source: 'CUSTOMER_CARD', customerCardId: id });
  if (form.paidTo && form.paidTo !== 'OTHER') body.paidToProviderId = form.paidTo;
  else if (form.paidToName.trim()) body.paidToName = form.paidToName.trim();
  return body;
};

// Who the payment went to is required: a listed provider, or a typed payee name.
export const missingPayee = (form) => !(form.paidTo && form.paidTo !== 'OTHER') && !(form.paidToName || '').trim();

export const paymentSourceText = (p, { accounts, tcCards }) => {
  if (p.source === 'PARTNER') return 'Paid by the partner';
  if (p.source === 'CUSTOMER_CARD') return p.customerCardLabel || 'Customer card';
  if (p.source === 'TC_CREDIT_CARD') { const c = tcCards.find((x) => x.id === p.sourceCardId); return c ? cardLabel(c) : 'Credit card'; }
  const a = accounts.find((x) => x.id === p.sourceAccountId);
  return a ? accountTitle(a) : p.source;
};

const PaymentSourceFields = ({ form, set, lookups, amount }) => {
  const { accounts, tcCards, customerCards, providers } = lookups;
  const payable = accounts.filter((a) => a.isActive !== false && PAYABLE_TYPES.includes(accountTypeOf(a)));
  const activeTcCards = tcCards.filter((c) => c.active !== false);
  const activeProviders = providers.filter((p) => p.active !== false);
  const selectedAccount = form.from.startsWith('ACCOUNT:') ? accounts.find((a) => a.id === form.from.split(':')[1]) : null;
  const balance = selectedAccount ? Number(selectedAccount.balance ?? NaN) : NaN;
  const goesNegative = amount != null && !Number.isNaN(balance) && balance - amount < 0;

  return (
    <>
      <TextField select fullWidth size="small" label="Paid from *" value={form.from} onChange={(e) => set({ from: e.target.value })}>
        <ListSubheader>Typing centre accounts</ListSubheader>
        {payable.map((a) => (
          <MenuItem key={a.id} value={`ACCOUNT:${a.id}`}>
            {accountTitle(a)}{accountTypeOf(a) === 'CASH' ? ' (cash)' : accountTypeOf(a) === 'E_WALLET' ? ' (e-wallet)' : ''}
          </MenuItem>
        ))}
        {activeTcCards.length > 0 && <ListSubheader>Typing centre credit cards</ListSubheader>}
        {activeTcCards.map((c) => <MenuItem key={c.id} value={`TCCARD:${c.id}`}>{cardLabel(c)}</MenuItem>)}
        <ListSubheader>Customer&apos;s saved cards</ListSubheader>
        {customerCards.length === 0 && <MenuItem value="CUSTCARD:">Customer&apos;s card (none saved)</MenuItem>}
        {customerCards.map((c) => <MenuItem key={c.id} value={`CUSTCARD:${c.id}`}>{c.label || `${c.cardType} •••• ${c.lastFour}`}</MenuItem>)}      </TextField>
      {goesNegative && (
        <Alert severity="warning" sx={{ py: 0 }}>This payment takes {accountTitle(selectedAccount)} below zero.</Alert>
      )}
      {form.from.startsWith('CUSTCARD:') && (
        <Typography variant="caption" color="text.secondary">
          Paid with the customer&apos;s card: no money leaves the typing centre, so no evidence is needed.
        </Typography>
      )}
      <Box display="flex" gap={1}>
        <TextField select size="small" label="Paid to" required sx={{ flex: 1 }} value={form.paidTo} onChange={(e) => set({ paidTo: e.target.value })}>
          {activeProviders.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          <MenuItem value="OTHER">Someone else…</MenuItem>
        </TextField>
        {form.paidTo === 'OTHER' && (
          <TextField size="small" label="Payee name" required sx={{ flex: 1 }} value={form.paidToName} onChange={(e) => set({ paidToName: e.target.value })} />
        )}
      </Box>
      <Box display="flex" gap={1}>
        <TextField size="small" label="Receipt / transaction ref" sx={{ flex: 1 }} value={form.receiptRef} onChange={(e) => set({ receiptRef: e.target.value })} />
        <TextField size="small" type="date" label="Paid on *" InputLabelProps={{ shrink: true }} inputProps={{ max: today() }}
          sx={{ width: 170 }} value={form.paidAt} onChange={(e) => set({ paidAt: e.target.value })} />
      </Box>
    </>
  );
};

export default PaymentSourceFields;
